import { useCallback, useEffect, useRef, useState } from 'react';

import type { SkillAccessRow } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

/**
 * ViewModel for the per-ROLE skill-access editor. Loads the reconciled view
 * (every live skill + granted flag, plus any stale grants) and exposes a
 * replace-set `save`. Async state follows the `{ idle | loading | success |
 * error }` convention.
 */
export function useRoleSkillAccess(roleId: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const [rows, setRows] = useState<SkillAccessRow[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();

  const fetch = useCallback(() => {
    if (!roleId) return;
    apiClient
      .getRoleSkillAccess(roleId)
      .then((res) => {
        setRows(res.data);
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load skill access');
        setStatus('error');
      });
  }, [apiClient, roleId]);

  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;

  useEffect(() => {
    if (!enabled || !roleId) return;
    setStatus('loading');
    fetchRef.current();
  }, [enabled, roleId]);

  const save = useCallback(
    async (skillIds: string[]) => {
      if (!roleId) return;
      setSaveStatus('saving');
      try {
        const res = await apiClient.setRoleSkillAccess(roleId, skillIds);
        setRows(res.data);
        setSaveStatus('success');
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to save skill access');
        setSaveStatus('error');
        throw err;
      }
    },
    [apiClient, roleId],
  );

  return { rows, status, saveStatus, error, reload: fetch, save };
}
